/**
 * Date night painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dateNightPaintingIdeasPack: ToilePack = {
    slug: 'date-night-painting-ideas',
    category: 'craft',
    name: 'Date night painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'lighthouse', label: 'Lighthouse', subject: 'two finished canvases of the same lighthouse at dusk, each painted a little differently'},
        {key: 'sunflowers', label: 'Sunflowers', subject: 'two finished canvases of sunflowers in a jug, brushes and two wine glasses beside them'},
        {key: 'skyline', label: 'Night skyline', subject: 'two finished canvases of a city skyline at night under a big moon'},
    ],
    styles: [
        {key: 'class', label: 'Class style', style: 'beginner acrylic with bold flat strokes, as painted at a paint-and-sip class', palette: 'bright mixed acrylics', framing: 'on a wooden table with paper cups of paint, used brushes and two glasses of red wine'},
        {key: 'dabbed', label: 'Dabbed', style: 'loose impressionist acrylic in short dabbed strokes', palette: 'soft blended pastels', framing: 'on a table covered in brown paper with paint-smeared palettes and two glasses of white wine'},
        {key: 'silhouette', label: 'Silhouette', style: 'silhouette style, a smooth blended sunset sky behind flat black shapes', palette: 'orange, pink and purple with black', framing: 'on a small café table with a candle in a jar, brushes in a glass and two glasses of rosé'},
    ],
    directions: ['a sailboat', 'autumn trees', 'cherry blossoms'],
    directionHint: 'try: a sailboat',
    seo: {
        title: 'Date Night Painting Ideas: Lighthouse, Sunflowers, Skyline',
        metaDescription: 'Nine date night painting ideas for two: a lighthouse at dusk, sunflowers and a night skyline, each painted side by side.',
        h1: 'Date night painting ideas: lighthouse at dusk, sunflowers, city skyline at night',
        tagline: 'Nine pairs of canvases, three subjects, each set on a table among brushes and glasses from a paint-and-sip evening.',
        intro: 'A paint-and-sip date night is two people, two easels and one subject, painted side by side. These nine show three subjects, each as two finished canvases: a lighthouse at dusk, sunflowers in a jug, and a city skyline at night under a big moon. Each set sits on a table among brushes and glasses. Type another subject, such as a sailboat or cherry blossoms, and the same evening carries it.',
        useCases: [
            'a paint-and-sip evening',
            "a couple's first painting together",
            'a gift for a partner who likes to paint',
            'a group night for friends',
        ],
        faq: [
            {q: 'Do you need to be an artist?', a: 'No. The class style shows bold, flat strokes of the kind a beginner paints in one sitting.'},
            {q: 'Are the two canvases the same?', a: 'They share one subject. In the lighthouse set, each canvas is painted a little differently.'},
            {q: 'What are the three table settings?', a: 'Paper cups of paint and red wine, brown paper with white wine, and a candle with rosé in a café setting.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the two-canvas format and the table setting carry it.'},
        ],
    },
}
